import { INPUT_TEXT_MAX_LENGTH } from '@/common/constants';
import {
    showErrorNotificationFunction,
    showSuccessNotificationFunction,
} from '@/common/helpers';
import i18n from '@/plugins/vue-i18n';
import yup from '@/plugins/yup';
import { ElLoading } from 'element-plus';
import { useField, useForm } from 'vee-validate';
import { ColumnType, ExportObjectListExcel } from '../constants';
import { projectPlanningService } from '../services/planning.service';
import { projectPlanningModule } from '../store';

const exportFormSchema = yup.object({
    selectedObjects: yup
        .array(
            yup
                .string()
                .oneOf([...Object.values(ExportObjectListExcel)])
                .required(),
        )
        .required(),
    fileName: yup.string().max(INPUT_TEXT_MAX_LENGTH).trim().required(),
    savePath: yup.string().required(),
    taskIds: yup.array(yup.string()).nullable(true),
    durationUnits: yup.string().notRequired().nullable(),
    resourceUnits: yup.string().notRequired().nullable(),
    includeLabelUnit: yup.boolean().required(),
});

export const setupExportExcelForm = () => {
    const initValues = {
        selectedObjects: [],
        fileName: '',
        savePath: '',
        taskIds: null,
        durationUnits: null,
        resourceUnits: null,
        includeLabelUnit: false,
    };

    const { handleSubmit, resetForm, setFieldValue, validate, errors } = useForm({
        initialValues: initValues,
        validationSchema: exportFormSchema,
    });

    const onSubmit = handleSubmit(async (values) => {
        if (!values.resourceUnits && !values.durationUnits) {
            showErrorNotificationFunction(
                i18n.global.t('planning.exportForm.message.unitNotFound'),
            );

            return;
        }
        if (!values.resourceUnits) {
            showErrorNotificationFunction(
                i18n.global.t('planning.exportForm.message.resourceUnitNotFound'),
            );

            return;
        }

        if (!values.durationUnits) {
            showErrorNotificationFunction(
                i18n.global.t('planning.exportForm.message.durationUnitNotFound'),
            );

            return;
        }
        const loading = ElLoading.service({});
        const taskViews = projectPlanningModule.planning?.view?.columns;

        const response = await projectPlanningService.exportPlanningToExcel(
            projectPlanningModule.planning?._id as string,
            {
                projectId: projectPlanningModule.planning?.projectId as string,
                selectedObjects: values.selectedObjects as string[],
                fileName: values.fileName as string,
                savePath: values.savePath as string,
                taskIds: values.taskIds as string[],
                durationUnits: values.durationUnits as string,
                resourceUnits: values.resourceUnits as string,
                includeLabelUnit: values.includeLabelUnit as boolean,
                taskColumns: taskViews
                    ?.filter((i) => i.type === ColumnType.PREDEFINED)
                    .map((i) => i.name),
            },
        );

        loading.close();

        if (response.success) {
            showSuccessNotificationFunction(
                i18n.global.t('planning.exportForm.message.exportSuccess'),
            );
            projectPlanningModule.setIsShowExportExcelPopup(false);
            return response.data;
        } else {
            showErrorNotificationFunction(response.message);
            return null;
        }
    });

    const { value: selectedObjects } = useField('selectedObjects');
    const { value: fileName } = useField('fileName');
    const { value: savePath } = useField('savePath');
    const { value: durationUnits } = useField('durationUnits');
    const { value: resourceUnits } = useField('resourceUnits');
    const { value: includeLabelUnit } = useField('includeLabelUnit');

    return {
        initValues,
        onSubmit,
        resetForm,
        setFieldValue,
        validate,
        errors,
        selectedObjects,
        fileName,
        savePath,
        durationUnits,
        resourceUnits,
        includeLabelUnit,
    };
};
